import OnspaceMediaPlayer from '../player'

import translation from '@onpace/onspace-core/components/translations'
import { registerDialogPlayerClass } from '@onpace/onspace-media/elements/player/dialog'

import OnspaceVideoPlayerControls from '@onpace/onspace-media/elements/player/video/controls'

const PRESENTATION_MODE_INLINE = 'inline'
const PRESENTATION_MODE_PICTURE_IN_PICTURE = 'pictureinpicture'
const PRESENTATION_MODE_FULL_SCREEN = 'fullscreen'
const PRESENTATION_MODE_REMOTE = 'remote'

const isTouchScreen = window.matchMedia('(pointer: coarse)').matches

/// The Onspace Video Player is a concrete subclass of OnspaceMediaPlayer capable of playing video content.
export default class OnspaceVideoPlayer extends OnspaceMediaPlayer {
  /// Sets up the media player element.
  ///
  /// Arguments are inherited from OnspaceMediaPlayer.runConstructor. Additionally, the +options+ object also supports
  /// the following:
  /// [startMuted]
  ///   Indicates if the player should begin playback without audio.
  /// [hideControls]
  ///   Indicates when the player should display controls. This supports the following values:
  ///   [never]
  ///     Never hide the controls. This is the default.
  ///   [inline]
  ///     Only hide controls when viewing inline. Controls will be available in other contexts such as full screen.
  ///   [always]
  ///     Always hide controls.
  /// [allowPictureInPicture]
  ///   Indicates if picture in picture should be allowed.
  runConstructor(options = {}) {
    super.runConstructor(options)

    this._exclusionBottom = 0
  }

  /// Runs when the video player is first connected to the DOM.
  runFirstConnected(options = {}) {
    this.multiPlayer = options.multiPlayer || null

    const playerOptions = options.options || {}
    this.startMuted = playerOptions.startMuted || false
    this.hideControls = playerOptions.hideControls || 'never'
    this.allowPictureInPicture = playerOptions.allowPictureInPicture
    if (typeof this.allowPictureInPicture !== 'boolean') { this.allowPictureInPicture = true }

    super.runFirstConnected(options)

    this.classList.add('onspace-player--video')

    this.addEventListener('fullscreenchange', this.fullscreenChanged.bind(this))
  }

  /// Responds to full screen changes on the element.
  fullscreenChanged(_event) {
    this.detectPresentationMode()
  }

  /// Sets the exclusion area from the bottom of the player.
  ///
  /// This ensures that the player does not render elements in the exclusion area.
  setExclusionBottom(height) {
    if (height === this._exclusionBottom) { return }
    this._exclusionBottom = height

    this.style.setProperty('--player-exclusion-bottom', `${height}px`)
    this.forceRenderTextTrack()
  }

  ////////// Actions

  /// Initialises and starts the media player.
  async startPlayer(options) {
    this.detectPresentationMode()

    await super.startPlayer(options)

    if (this.startMuted) {
      this.disableAudio()
    }

    this.detectPresentationMode()
  }

  /// Initialises and resumes the media player using an existing playback session.
  continuePlayer() {
    super.continuePlayer()

    this.detectPresentationMode()
  }

  /// Stops and destroys the media player components.
  ///
  /// This overrides OnspaceMediaPlayer.destroyPlayer to also exit Picture-In-Picture if necessary.
  destroyPlayer() {
    this.exitPictureInPicture()

    super.destroyPlayer()
  }

  ////////// Native Player

  /// Initialises a newly created native player element.
  ///
  /// This overrides OnspaceMediaPlayer.initializeNativePlayerElement to add additional configuration specific to video
  /// content.
  initializeNativePlayerElement() {
    super.initializeNativePlayerElement()

    this.nativePlayer.addEventListener('enterpictureinpicture', this.nativePlayerEnteredPictureInPicture.bind(this))
    this.nativePlayer.addEventListener('leavepictureinpicture', this.nativePlayerLeftPictureInPicture.bind(this))
  }

  /// Creates the native player DOM element.
  ///
  /// This overrides OnspaceMediaPlayer.createNativePlayerElement to create a +video+ element.
  createNativePlayerElement() {
    const element = document.createElement('video')
    element.setAttribute('playsinline', '')

    return element
  }

  ///// Events

  /// Responds to the native player loading metadata.
  ///
  /// This overrides OnspaceMediaPlayer.nativePlayerLoadedMetadata to trigger events specific to video media.
  nativePlayerLoadedMetadata(event) {
    super.nativePlayerLoadedMetadata(event)

    this.triggerEvent('onspace:media:player:video-resolution-changed')
  }

  /// Responds to the native player updating its airplay target device.
  ///
  /// This overrides OnspaceMediaPlayer.nativePlayerAirplayTargetChanged to update the current presentation mode.
  nativePlayerAirplayTargetChanged(event) {
    super.nativePlayerAirplayTargetChanged(event)

    this.detectPresentationMode()
  }

  /// Responds to the native player entering picture in picture mode.
  nativePlayerEnteredPictureInPicture(_event) {
    this.detectPresentationMode()
  }

  /// Responds to the native player leaving picture in picture mode.
  nativePlayerLeftPictureInPicture(_event) {
    this.detectPresentationMode()
  }

  ////////// HLS

  /// Responds to the currently playing hls level updating.
  ///
  /// This overrides OnspaceMediaPlayer.nativePlayerLoadedMetadata to trigger events specific to video media.
  hlsLevelSwitched(event, data) {
    super.hlsLevelSwitched(event, data)

    this.triggerEvent('onspace:media:player:video-resolution-changed')
  }

  ///// Google Cast

  /// Indicates if the player can currently use Google Cast.
  get canGoogleCast() {
    return super.canGoogleCast && (!this.supportsMultiScreen || this.multiPlayer.playerCanGoogleCast)
  }

  /// Responds to a change in the Google Cast connection state.
  ///
  /// This overrides OnspaceMediaPlayer.googleCastConnectedChanged to update the current presentation mode.
  googleCastConnectedChanged(event) {
    super.googleCastConnectedChanged(event)

    this.detectPresentationMode()
  }

  /// Responds to a change in the Google Cast player's loaded media.
  ///
  /// This overrides OnspaceMediaPlayer.googleCastMediaLoadedChanged to update the current presentation mode.
  googleCastMediaLoadedChanged(event) {
    super.googleCastMediaLoadedChanged(event)

    this.detectPresentationMode()
  }

  ////////// Controls

  /// Initialises and configures the controls element.
  ///
  /// This overrides OnspaceMediaPlayer.setupControls to disable controls when used by multi-screen.
  setupControls() {
    if (this.supportsMultiScreen) { return }

    super.setupControls()
  }

  /// Creates a controls element.
  createControlsElement() {
    return new OnspaceVideoPlayerControls({ player: this })
  }

  /// Indicates if the controls element should be removed.
  get shouldDetachControls() {
    switch (this.hideControls) {
    case 'always': return this.playbackStarted
    case 'inline': return !this.fullscreenActive && this.playbackStarted
    case 'never':
    default:
      return false
    }
  }

  /// Indicates if the player can be closed.
  get hasCloseAction() {
    if (this.fullscreenActive && !this.multiPlayer) { return isTouchScreen }

    return super.hasCloseAction
  }

  /// Closes the player if possible.
  performCloseAction() {
    if (this.fullscreenActive && !this.multiPlayer) {
      if (isTouchScreen) { this.exitFullscreen() }
      return
    }

    super.performCloseAction()
  }

  ////////// Multi Screen

  /// Indicates if the video supports multi screen playback.
  get supportsMultiScreen() {
    return !!this.multiPlayer
  }

  ////////// Presentation

  /// Determines the current presentation mode, updating the state if required.
  detectPresentationMode() {
    let presentationMode
    if (this.fullscreenActive) {
      presentationMode = PRESENTATION_MODE_FULL_SCREEN
    } else if (this.pictureInPictureActive) {
      presentationMode = PRESENTATION_MODE_PICTURE_IN_PICTURE
    } else if (this.remotePlaybackActive) {
      presentationMode = PRESENTATION_MODE_REMOTE
    } else {
      presentationMode = PRESENTATION_MODE_INLINE
    }

    if (presentationMode !== this.presentationMode) {
      const previousPresentationMode = this.presentationMode
      this.presentationMode = presentationMode

      this.classList.remove(`onspace-player--presentation-${previousPresentationMode}`)
      this.classList.add(`onspace-player--presentation-${presentationMode}`)

      if (presentationMode === PRESENTATION_MODE_PICTURE_IN_PICTURE) {
        this.showOverlayMessage(translation('onspace.media.player.playback.video_picture_in_picture'), { icon: 'onspace/player_pip' })
        this.beginPersisting()
      } else if (previousPresentationMode === PRESENTATION_MODE_PICTURE_IN_PICTURE) {
        this.clearMessages()
        this.endPersisting(true)
      }
    }

    this.setupControls()
    this.updateCoverCloseButton()

    this.triggerEvent('onspace:media:player:remote-changed')
    this.triggerEvent('onspace:media:player:close-action-changed')
    this.triggerEvent('onspace:media:player:video-presentation-changed')
  }

  ///// Picture-in-Picture

  /// Indicates if the video supports picture in picture.
  get supportsPictureInPicture() {
    return document.pictureInPictureEnabled
  }

  /// Indicates if the player can currently enter picture in picture.
  get canEnterPictureInPicture() {
    return this.supportsPictureInPicture && this.allowPictureInPicture && this.presentationMode === PRESENTATION_MODE_INLINE && (!this.supportsMultiScreen || this.multiPlayer.playerCanEnterPictureInPicture)
  }

  /// Indicates if the player is currently in picture in picture mode.
  get pictureInPictureActive() {
    return !!this.nativePlayer && document.pictureInPictureElement === this.nativePlayer
  }

  /// Enters or exits picture in picture mode, depending on the current state.
  togglePictureInPicture() {
    if (this.pictureInPictureActive) {
      this.exitPictureInPicture()
    } else {
      this.enterPictureInPicture()
    }
  }

  /// Enters the player into picture in picture mode.
  ///
  /// This will do nothing unless the player +canEnterPictureInPicture+.
  enterPictureInPicture() {
    if (!this.canEnterPictureInPicture || !this.canEnterPictureInPicture) { return }

    this.nativePlayer.requestPictureInPicture()
  }

  /// Exists the player from picture in picture mode.
  exitPictureInPicture() {
    if (!this.pictureInPictureActive) { return }

    document.exitPictureInPicture()
  }

  ///// Full Screen

  /// Indicates if the browser supports taking any element full screen.
  get supportsDocumentFullscreen() {
    return document.fullscreenEnabled
  }

  /// Indicates if the video supports entering full screen mode.
  get supportsVideoFullscreen() {
    return true
  }

  /// Indicates if the player can currently enter full screen mode.
  get canEnterFullscreen() {
    return (this.supportsDocumentFullscreen || this.supportsVideoFullscreen) && this.presentationMode === PRESENTATION_MODE_INLINE
  }

  /// Indicates if the player is currently in full screen mode.
  get fullscreenActive() {
    return !!document.fullscreenElement
  }

  /// Enters or exits full screen mode, depending on the current state.
  toggleFullscreen() {
    if (this.fullscreenActive) {
      this.exitFullscreen()
    } else {
      this.enterFullscreen()
    }
  }

  /// Enters the player into full screen mode.
  ///
  /// This will attempt to enter full screen mode using the Document Full Screen API, otherwise the HTMLVideoElement
  /// API. This will do nothing unless the player +canEnterFullScreen+.
  enterFullscreen() {
    if (!this.canEnterFullscreen) { return }

    if (this.supportsDocumentFullscreen) {
      this.requestFullscreen()
    } else {
      this.nativePlayer.requestFullscreen()
    }
  }

  /// Exits the player from full screen mode.
  exitFullscreen() {
    if (!this.fullscreenActive) { return }

    document.exitFullscreen()
  }

  ////////// Analytics

  /// Configures analytics parameters.
  ///
  /// This overrides OnspaceMediaPlayer.configureAnalytics to additionally set the +viewer_type+.
  configureAnalytics() {
    super.configureAnalytics()

    this.analyticsParams.content_type = 'video'
  }
}

window.customElements.define('onspace-video', OnspaceVideoPlayer)
registerDialogPlayerClass('video', OnspaceVideoPlayer)
